// Funciones puras de presentación del panel (spec panel-atencion §4).
// Cero IO: sin React, sin supabase-js, sin DOM.

import type { LlamadaEventoPanel, LlamadaRow } from '../../types/llamadas';

export type EstadoVisual = 'en_curso' | 'procesando' | 'resuelta' | 'escalada';

// Usa solo campos con semántica definida en registro-llamadas: `estado` +
// `exitosa` (call_successful del análisis de Retell). exitosa=null en una
// analizada cuenta como no exitosa (escalada), nunca como resuelta.
export function estadoVisual(l: LlamadaRow): EstadoVisual {
  if (l.estado === 'en_curso') return 'en_curso';
  if (l.estado === 'finalizada') return 'procesando';
  return l.exitosa === true ? 'resuelta' : 'escalada';
}

// Texto del estado tal como lo enseña el cuadro, en la tabla y en la
// exportación. Vive aquí y no en el componente para que la tabla y el Excel no
// puedan decir cosas distintas (change 003 del cuadro).
export const ETIQUETA_ESTADO: Record<EstadoVisual, string> = {
  en_curso: 'En curso',
  procesando: 'Procesando',
  resuelta: 'Resuelta por IA',
  escalada: 'Escalada',
};

export function textoEstado(l: LlamadaRow): string {
  return ETIQUETA_ESTADO[estadoVisual(l)];
}

export type DestinoEscalado = 'operaciones' | 'comercial' | 'oficina';

// A quién se derivó una llamada escalada, EN UN SOLO SITIO. De aquí salen la columna
// «Derivado a» de la tabla, la celda del Excel, el panel «Derivaciones» del cuadro —que va
// en el PDF— y la línea de tiempo del detalle de Atención. Estaba escrito tres veces con
// la misma forma, y una de las tres nombraba distinto el tercer cubo: la misma llamada
// escalada con motivo `administrativo` salía como «Otros escalados» en el cuadro y como
// «derivada a Oficina» en el detalle al que lleva el enlace de esa misma fila (vuelta 15
// del gate). `null` si no se escaló: la tabla lo pinta «—» y el Excel lo deja vacío, que es
// lo que se puede filtrar y contar.
export function destinoEscalado(l: LlamadaRow): DestinoEscalado | null {
  if (estadoVisual(l) !== 'escalada') return null;
  if (l.motivo === 'operacion') return 'operaciones';
  if (l.motivo === 'comercial') return 'comercial';
  return 'oficina';
}

// DOS etiquetas para el mismo destino, a propósito, porque el registro es distinto: la
// columna y la barra nombran un CUBO —«Otros escalados» dice justamente que ahí cae todo
// lo demás— y la línea de tiempo completa una frase, donde «derivada a Otros escalados» no
// se puede leer. Lo que NO puede divergir es el REPARTO, y de eso se encarga
// `destinoEscalado`: estos dos mapas solo ponen palabras.
const ETIQUETA_DESTINO: Record<DestinoEscalado, string> = {
  operaciones: 'Operaciones',
  comercial: 'Comercial',
  oficina: 'Otros escalados',
};

const DESTINO_EN_FRASE: Record<DestinoEscalado, string> = {
  operaciones: 'Operaciones',
  comercial: 'Comercial',
  oficina: 'Oficina',
};

export function derivadoA(l: LlamadaRow): string | null {
  const destino = destinoEscalado(l);
  return destino === null ? null : ETIQUETA_DESTINO[destino];
}

export interface MensajeTranscripcion {
  rol: 'bot' | 'cliente';
  texto: string;
}

const PREFIJO_TURNO = /^\s*(Agent|User)\s*:\s*(.*)$/;

// Parsea el transcript plano de Retell ("Agent: ...\nUser: ...") a burbujas.
// Líneas sin prefijo reconocible se anexan al mensaje anterior (saltos de
// línea del propio turno); sin turno previo, se descartan. null/'' → [].
export function parseTranscripcion(transcripcion: string | null): MensajeTranscripcion[] {
  if (!transcripcion) return [];
  const mensajes: MensajeTranscripcion[] = [];
  for (const linea of transcripcion.split('\n')) {
    const m = PREFIJO_TURNO.exec(linea);
    if (m) {
      mensajes.push({ rol: m[1] === 'Agent' ? 'bot' : 'cliente', texto: (m[2] ?? '').trim() });
    } else if (linea.trim() !== '') {
      const previo = mensajes.at(-1);
      if (previo) previo.texto = `${previo.texto}\n${linea.trim()}`;
    }
  }
  return mensajes;
}

// Duración m:ss (o '—' sin dato). Compartida por feed, detalle, KPIs y cuadro.
export function fmtDuracion(s: number | null): string {
  if (s === null) return '—';
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}

// Hora HH:MM en Europe/Madrid (o '—' sin dato). Una fecha ilegible cuenta como
// "sin dato": Intl.format lanza RangeError con un Invalid Date, y esta función
// la usan el feed, el detalle y el cuadro — un valor raro no debe tumbar la
// pantalla entera por un formato de hora.
export function horaMadrid(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  return new Intl.DateTimeFormat('es-ES', {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    minute: '2-digit',
  }).format(t);
}

// Etiquetas de la taxonomía de motivos (triaje del bot). Compartidas por el
// feed de Atención y las agregaciones del cuadro de mando.
export const ETIQUETA_MOTIVO: Record<string, string> = {
  rechazable: 'Llamada rechazable',
  administrativo: 'Gestión administrativa',
  comercial: 'Consulta comercial',
  operacion: 'Operación · avería',
  otro: 'Otro motivo',
  sin_motivo: 'Sin clasificar',
};

// «Esto no muestra NADA»: blancos, formato, control, marcas y lo que Unicode declara
// ignorable por defecto. Un texto compuesto sólo de estos pinta un hueco, así que como
// nombre no vale — y la pregunta de `nombreDeCliente` es «¿se ve algo?», no «¿es veraz?».
//
// La clase es una COPIA de la que usa la frontera del dato (el `asStr` del webhook de
// Retell y el del adaptador del sync, en el cambio `registro-llamadas`) y no un import a
// propósito: una de esas copias corre en Deno dentro de una Edge Function y no comparte
// módulos con el frontend, viven en otro cambio con su propio gate, y sobre todo
// contestan otra pregunta —«¿es un valor?», al ESCRIBIR— mientras esta decide «¿merece
// pintarse?» sobre el dato ya guardado. Si divergen no es un fallo: son dos decisiones.
//
// Sin la bandera `g`: `test` con `lastIndex` compartido daría falsos negativos alternos.
const NADA_QUE_PINTAR = /^[\s\p{Cf}\p{Cc}\p{M}\p{Default_Ignorable_Code_Point}]*$/u;

// Lo que queda FUERA, y la lista está ABIERTA: los puntos de código que Unicode no
// declara ignorables y que aun así se pintan en blanco porque son letra o símbolo con
// glifo propio. U+2800 (braille en blanco) y los jeroglíficos U+13441–U+13443 son los
// que se han encontrado; no hay ninguna propiedad que los agrupe, y en este gate el
// límite se escribió como lista cerrada tres veces y las tres estaba incompleto. Un
// nombre así seguirá saliendo como un hueco.
function _sePinta(texto: string | null): string | null {
  return texto !== null && !NADA_QUE_PINTAR.test(texto) ? texto : null;
}

// Cómo se NOMBRA a un cliente, en un solo sitio. Lo usan el desplegable y el panel de
// clientes del cuadro (vía `etiquetaCliente`), la tabla del cuadro, el Excel, la ficha de
// Atención y la fila del feed: era la misma cadena copiada, y el gate ha ido encontrando
// una superficie por vuelta que se desviaba. La razón social puede ser cadena vacía
// —`clientes_replica.razon_social` es `text NOT NULL` pero nada prohíbe el vacío—, y con
// `??` se daba por buena: barra sin nombre en el panel que va al PDF, opción en blanco en
// el desplegable y celda vacía en el Excel. Con `||` se daban por buenos `'   '` y
// `'\u200B'`, que son veraces y también pintan un hueco: una llamada CON cliente
// identificado se quedaba visualmente sin nombre en las seis superficies en vez de caer a
// su id de Odoo. Se decide por «esto no muestra nada», y para los DOS argumentos — el id
// de Odoo es `text` en la réplica y puede venir igual.
//
// Esto NO RECORTA, y es el límite exacto: un nombre con algo visible más invisibles sale
// ENTERO. `'\u200BTalleres SL'` y `'Talleres SL'` siguen siendo dos valores distintos que
// se leen igual, y eso lo cierra la frontera al escribir, no una función de pantalla.
// Aquí sólo se rechaza lo que no muestra nada en absoluto.
//
// Ojo: esto NO decide si HAY cliente. Eso lo contesta `id_cliente`, y decidirlo por la
// veracidad del nombre es el otro error de la familia.
export function nombreDeCliente(razonSocial: string | null, idOdoo: string | null): string {
  return _sePinta(razonSocial) ?? _sePinta(idOdoo) ?? 'Cliente sin nombre';
}

// Etiqueta de un motivo, o el propio motivo si no tiene. Con `Object.hasOwn` y no
// con `ETIQUETA_MOTIVO[m]`: el motivo lo escribe el análisis de la llamada sin
// validar, y «constructor» o «toString» devolverían una función heredada de
// Object, que el `??` no frena (fase 8 del change 003 de cuadro-mando, AD-P2-1).
//
// LÍMITE, y a propósito: el cinturón de las pantallas cubre la AUSENCIA del motivo
// (`null`) y la CADENA VACÍA, nada más. `NADA_QUE_PINTAR` no se aplica aquí. Un motivo
// visualmente vacío (`'  '`, `'\u200B'`) o con invisibles delante (`'\u200Bcomercial'`)
// sigue dando una celda que parece en blanco y dos barras que se leen igual en el panel
// «Motivos» que va en el PDF, hasta que lo corte la frontera del dato.
//
// La razón de no traerlo: «¿hay motivo?» se contesta en DIEZ puntos de la capa de
// pantallas —contados, con su sitio—. Seis sobre el motivo de la llamada:
// `DetalleLlamada` (el motivo del detalle), `FilaLlamada` (la fila del feed),
// `TablaLlamadas` (la celda), `filasExportables` (la celda del Excel),
// `distribucionMotivos` (la clave de agrupación de las barras) y `opcionesDisponibles`
// (qué chips existen). Y cuatro sobre el motivo del FILTRO, donde además se decide por
// nulidad y está bien, porque ese valor lo pone la pantalla y nunca es cadena vacía:
// `describirFiltros` (la cabecera impresa), `hayFiltrosActivos`, `aplicarFiltros` y
// `nombreExportacion` (el nombre del fichero). El chip activo compara por igualdad, así
// que no es uno de los diez. Cada uno dice a propósito algo distinto cuando no hay
// motivo —«—», «Sin clasificar», «Motivo pendiente»—, y repartir el saneado por diez
// sitios es la forma de cadena que este proyecto ya ha fallado, con el compilador
// callado. Lo mismo vale para `etiquetaRama`.
export function etiquetaMotivo(motivo: string): string {
  return Object.hasOwn(ETIQUETA_MOTIVO, motivo) ? (ETIQUETA_MOTIVO[motivo] ?? motivo) : motivo;
}

// Etiquetas de las ramas del grupo, compartidas por los chips del cuadro, la cabecera
// del informe impreso (vía `describirFiltros`) y el detalle de Atención. Esos tres son
// hoy todos los sitios que escriben una rama.
//
// Viven aquí y no en `lib/cuadro/filtros.ts`, donde nacieron, porque el detalle de
// Atención las necesita y Atención NO puede depender de `lib/cuadro`: `lib/llamadas` es
// el módulo del que ya dependen las dos pantallas. Mientras estuvieron sólo en el
// cuadro, el detalle pintaba `rama` en crudo con `text-transform: capitalize` y decía
// «Aramatica», sin acento, en el caso NORMAL, mientras los otros dos decían
// «Aramática»: el nombre del cliente mal escrito en cada llamada.
export const ETIQUETA_RAMA: Record<string, string> = {
  aramatica: 'Aramática',
  siwell: 'Siwell',
};

// La rama sale del dato, igual que el motivo: con `Object.hasOwn` (ver `etiquetaMotivo`),
// y con el mismo límite del cinturón escrito ahí.
export function etiquetaRama(rama: string): string {
  return Object.hasOwn(ETIQUETA_RAMA, rama) ? (ETIQUETA_RAMA[rama] ?? rama) : rama;
}

// --- Línea de tiempo del detalle (log-resolucion-odoo) ---

export interface PasoLog {
  hora: string; // HH:MM en Madrid, o '—' cuando no hay hora que enseñar
  texto: string;
  pendiente?: boolean; // paso que aún no ha ocurrido (los de Odoo)
  // De dónde sale el paso: de un hito real de la bitácora o deducido de la
  // fila. No es decorativo — es lo que permite distinguir los dos caminos
  // cuando ambos producen las mismas horas.
  origen: 'evento' | 'reconstruido';
}

// A quién se deriva, con el reparto de `destinoEscalado` y la etiqueta del registro de
// frase. Aquí se llama con una llamada que ya se sabe escalada, así que el `?? 'oficina'`
// no es una decisión: es el tipo.
function _derivadoA(l: LlamadaRow): string {
  return DESTINO_EN_FRASE[destinoEscalado(l) ?? 'oficina'];
}

// Texto del hito de análisis. Compartido por los dos caminos a propósito: si
// cada uno tuviera el suyo, con el tiempo divergirían y la misma llamada se
// leería distinta según tuviese bitácora o no.
function _textoAnalisis(l: LlamadaRow): string {
  const como = l.resultado ? ` — ${l.resultado.replace(/\.$/, '')}` : '';
  return estadoVisual(l) === 'escalada'
    ? `Analizada y derivada a ${_derivadoA(l)}${como}`
    : `Analizada · resuelta por el bot${como}`;
}

// Hito de la bitácora que anota el parte ya creado en Odoo: `destino` lleva su nº
// y `motivo`, la etapa en que nació. Lo escribirá el flujo que cree el parte (con
// el método `external_create_or_update_call` de Ingeos); hoy se anota a mano.
// A diferencia de los hitos del ciclo de vida, NO va con un UPDATE de
// `llamadas.actualizado_en`: el camino reconstruido usa esa columna como hora del
// análisis y tocarla la movería. Así que el hito no hace recargar a un detalle ya
// abierto: le llega por Realtime o con la siguiente recarga (reabrir la llamada o
// un UPDATE posterior de su fila). Si pierde el INSERT, hasta entonces (SD14).
export const TIPO_PARTE_CREADO = 'parte_creado';

// El último hito del parte de ESA llamada, o null. La bitácora llega ordenada por
// `ts`, así que el último que aparece es el más reciente. Filtra por `call_id`
// porque, al cambiar de llamada, `useLlamadaEventos` conserva la bitácora de la
// anterior hasta que llega la nueva: sin el filtro, su parte se colaría en esta.
export function hitoParteCreado(
  eventos: LlamadaEventoPanel[],
  callId: string
): LlamadaEventoPanel | null {
  for (let i = eventos.length - 1; i >= 0; i--) {
    const e = eventos[i];
    if (e?.tipo === TIPO_PARTE_CREADO && e.call_id === callId) return e;
  }
  return null;
}

// Los pasos que dependen de la conexión con Odoo, mientras son promesas. Se
// enseñan para que Manuel vea el recorrido completo, marcados como pendientes, y
// la del parte se retira en cuanto la bitácora trae el parte de verdad. El aviso
// de una escalada no: es otro objeto de Odoo y un parte no lo da por hecho.
export function _pasosOdooPendientes(l: LlamadaRow, eventos: LlamadaEventoPanel[] = []): PasoLog[] {
  if (l.estado !== 'analizada') return [];
  if (estadoVisual(l) === 'escalada') {
    return [
      {
        hora: '—',
        texto: 'Aviso en Odoo (responsable y nº de registro) · pendiente de conexión',
        pendiente: true,
        origen: 'reconstruido',
      },
    ];
  }
  if (l.motivo === 'operacion') {
    if (eventos.some((e) => e.tipo === TIPO_PARTE_CREADO)) return [];
    return [
      {
        hora: '—',
        texto: 'Parte de avería en Odoo (nº y técnico asignado) · pendiente de conexión',
        pendiente: true,
        origen: 'reconstruido',
      },
    ];
  }
  return [];
}

// Hito que cubre cada paso reconstruido, para poder cruzarlo con la bitácora.
// Interno: no forma parte del contrato de PasoLog.
interface PasoConHito extends PasoLog {
  hito: string;
  instante: number; // epoch ms, para ordenar mezclando los dos orígenes
}

// Orden canónico del relato de una llamada, para desempatar hitos que caen en
// el mismo instante (`llamada_finalizada` y `llamada_analizada` comparten el
// `end_timestamp` por P5). Un tipo desconocido va al final de su instante: es
// lo que se espera de los eventos de Odoo, que ocurren después.
const RANGO_HITO: Record<string, number> = {
  llamada_recibida: 0,
  transfer_started: 1,
  transfer_bridged: 1,
  transfer_cancelled: 1,
  transfer_ended: 1,
  llamada_finalizada: 2,
  llamada_analizada: 3,
  [TIPO_PARTE_CREADO]: 4,
};
const _rangoHito = (hito: string): number => RANGO_HITO[hito] ?? 99;

// Camino de siempre: la línea de tiempo deducida de las columnas de la propia
// fila. Se conserva para el histórico y el paquete demo, que no tienen bitácora
// (decisión G2: sin backfill, los dos caminos conviven).
export function _pasosReconstruidos(l: LlamadaRow): PasoConHito[] {
  const pasos: PasoConHito[] = [];
  if (l.inicio) {
    pasos.push({
      hora: horaMadrid(l.inicio),
      texto: 'Llamada recibida',
      origen: 'reconstruido',
      hito: 'llamada_recibida',
      instante: Date.parse(l.inicio),
    });
  }
  if (l.fin) {
    pasos.push({
      hora: horaMadrid(l.fin),
      texto: `Llamada finalizada · duración ${fmtDuracion(l.duracion_s)}`,
      origen: 'reconstruido',
      hito: 'llamada_finalizada',
      instante: Date.parse(l.fin),
    });
  }
  if (l.estado === 'analizada') {
    // actualizado_en ≈ hora del análisis en el flujo real (el webhook analiza al
    // momento); si no cae en el día de la llamada (p.ej. datos re-sembrados), no
    // se enseña una hora que confunde.
    const mismoDia =
      l.inicio !== null &&
      horaMadrid(l.actualizado_en) !== '—' &&
      new Date(l.actualizado_en).toDateString() === new Date(l.inicio).toDateString();
    pasos.push({
      hora: mismoDia ? horaMadrid(l.actualizado_en) : '—',
      texto: _textoAnalisis(l),
      origen: 'reconstruido',
      hito: 'llamada_analizada',
      // Para ordenar usamos el fin de la llamada aunque la hora mostrada sea la
      // de `actualizado_en`: el análisis siempre va después del fin, y así no se
      // cuela por delante cuando `actualizado_en` es de otro día.
      instante: Date.parse(l.fin ?? l.actualizado_en),
    });
  }
  return pasos;
}

// Etiquetas de los hitos conocidos. Los `transfer_*` conservan el nombre crudo
// de Retell porque son literalmente su evento; los nuestros van en español.
function _textoEvento(e: LlamadaEventoPanel, l: LlamadaRow): string {
  switch (e.tipo) {
    case 'llamada_recibida':
      return 'Llamada recibida';
    case 'llamada_finalizada':
      return `Llamada finalizada · duración ${fmtDuracion(l.duracion_s)}`;
    case 'llamada_analizada':
      return _textoAnalisis(l);
    // "Derivación" y no "transferencia": es el vocabulario que ya usan el feed
    // y el cuadro de mando para lo mismo.
    case 'transfer_started':
      return `Derivación iniciada${e.destino ? ` · ${e.destino}` : ''}`;
    case 'transfer_bridged':
      return `Derivación atendida${e.destino ? ` · ${e.destino}` : ''}`;
    case 'transfer_cancelled':
      return 'Derivación cancelada';
    case 'transfer_ended':
      return `Derivación finalizada${e.motivo ? ` · ${e.motivo}` : ''}`;
    case TIPO_PARTE_CREADO: {
      const detalle = [e.destino ? `nº ${e.destino}` : null, e.motivo].filter(Boolean).join(' · ');
      return detalle ? `Parte creado en Odoo · ${detalle}` : 'Parte creado en Odoo';
    }
    default: {
      // Compatibilidad hacia adelante: un tipo que esta versión de la UI no
      // conoce (los eventos de Odoo, cuando lleguen) se enseña en crudo en vez
      // de romper el detalle o desaparecer sin dejar rastro.
      const detalle = [e.destino, e.motivo].filter(Boolean).join(' · ');
      return detalle ? `${e.tipo} · ${detalle}` : e.tipo;
    }
  }
}

// Camino con bitácora: un paso por hito real. El orden viene ya resuelto por el
// hook (ts y, a igualdad, creado_en); aquí solo se estabiliza por ts para que un
// consumidor descuidado no lo pinte al revés.
export function _pasosDesdeEventos(l: LlamadaRow, eventos: LlamadaEventoPanel[]): PasoConHito[] {
  return eventos.map((e) => ({
    hora: horaMadrid(e.ts),
    texto: _textoEvento(e, l),
    origen: 'evento' as const,
    hito: e.tipo,
    instante: Date.parse(e.ts) || 0,
  }));
}

// Línea de tiempo del detalle.
//
// NO es "o bitácora o reconstrucción": es hito a hito. Cada hito del ciclo de
// vida que la bitácora tenga se pinta desde ella; el que falte se completa con
// la reconstrucción de la fila. Importa porque el registro de hitos es
// best-effort (P1): si una sola RPC falla, la llamada queda con bitácora
// PARCIAL, y bifurcar por `eventos.length > 0` haría desaparecer los pasos que
// no llegaron a anotarse en vez de deducirlos. Lo mismo aplica a una llamada
// que solo tenga eventos de derivación.
//
// Los `transfer_*` no tienen equivalente reconstruido: solo existen si están en
// la bitácora, y por eso son la señal de que la línea de tiempo es real.
export function pasosLog(l: LlamadaRow, eventos: LlamadaEventoPanel[]): PasoLog[] {
  // Solo la bitácora de ESTA llamada: al cambiar de llamada, `useLlamadaEventos`
  // conserva la de la anterior hasta que llega la nueva, y sin el filtro sus hitos
  // —y su parte, que además retiraría la promesa— se pintarían aquí mientras tanto.
  const propios = eventos.filter((e) => e.call_id === l.call_id);
  const desdeEventos = _pasosDesdeEventos(l, propios);
  const yaCubiertos = new Set(desdeEventos.map((p) => p.hito));
  const complemento = _pasosReconstruidos(l).filter((p) => !yaCubiertos.has(p.hito));

  // El desempate por rango NO es cosmético. `Array.sort` es estable, así que a
  // igual `instante` mandaría el orden de concatenación (primero los eventos,
  // luego los reconstruidos): con una bitácora a la que le falte
  // `llamada_finalizada`, "Analizada" se pintaría ANTES que "Llamada
  // finalizada", las dos a la misma hora. El orden del relato no puede depender
  // de qué hito le tocó estar en la bitácora.
  const pasos = [...desdeEventos, ...complemento].sort(
    (a, b) => a.instante - b.instante || _rangoHito(a.hito) - _rangoHito(b.hito)
  );
  return [...pasos, ..._pasosOdooPendientes(l, propios)];
}

export interface KpisDia {
  total: number;
  resueltasPct: number | null; // % de analizadas con exitosa=true; null si no hay analizadas
  escaladas: number; // analizadas con exitosa != true
  duracionMediaS: number | null; // media de duracion_s no nulos; null si no hay
}

export function kpisDia(llamadas: LlamadaRow[]): KpisDia {
  const analizadas = llamadas.filter((l) => l.estado === 'analizada');
  const resueltas = analizadas.filter((l) => l.exitosa === true);
  const duraciones = llamadas.map((l) => l.duracion_s).filter((d): d is number => d !== null);

  return {
    total: llamadas.length,
    resueltasPct:
      analizadas.length > 0 ? Math.round((resueltas.length / analizadas.length) * 100) : null,
    escaladas: analizadas.length - resueltas.length,
    duracionMediaS:
      duraciones.length > 0
        ? Math.round(duraciones.reduce((a, b) => a + b, 0) / duraciones.length)
        : null,
  };
}

// --- Equipo de la llamada (feature equipo-de-la-llamada) ---

export type EstadoEquipo = 'identificado' | 'descrito' | 'sin_dato';

// Tres estados, y el tercero NO es "no identificada": las llamadas anteriores a
// esta feature no tienen el dato porque nunca se pidió, no porque se intentara y
// fallara. Decir "no identificada" ahí sería contar una historia que no pasó.
export function estadoEquipo(l: LlamadaRow): EstadoEquipo {
  if (l.id_equipo) return 'identificado';
  // Cuenta como descrito tanto el texto libre como un id que no validó: en los
  // dos casos hay algo que decir, aunque no sea una puerta de su ficha.
  if (l.equipo_descrito || l.equipo_id_odoo) return 'descrito';
  return 'sin_dato';
}

// El subtítulo: de qué fuente viene el dato. Extraído y con test porque la
// revisión adversarial demostró que se podía revertir a la versión que el verify
// había declarado falsa —la que atribuía a quien llamaba algo que dijo la
// máquina— sin que cayera un solo test de los 299.
export function subtituloEquipo(l: LlamadaRow): string | null {
  const estado = estadoEquipo(l);
  if (estado === 'sin_dato') return null;
  if (estado === 'identificado') {
    return `Equipo de su ficha${l.equipo_origen ? ` · ${l.equipo_origen}` : ''}`;
  }
  if (l.equipo_descrito) return 'Descrito por quien llamó — no consta en su ficha';
  // Tercera rama: solo hay un id que dijo el bot y que no es de este cliente.
  // No lo describió nadie.
  return 'El asistente no pudo identificar la puerta';
}

// Qué se pinta. El id crudo sin resolver no se enseña tal cual —un número de
// Odoo no le dice nada a nadie— pero tampoco se esconde que hubo algo.
export function etiquetaEquipo(l: LlamadaRow): string | null {
  const estado = estadoEquipo(l);
  if (estado === 'sin_dato') return null;
  if (estado === 'identificado') {
    const ref = l.equipo?.referencia ?? null;
    const tipo = l.equipo?.tipo ?? null;
    if (ref && tipo) return `${ref} · ${tipo}`;
    return ref ?? tipo ?? 'Equipo de su ficha';
  }
  return l.equipo_descrito ?? 'No consta en su ficha';
}
